import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MailPlus, XCircle } from '@/components/icons';
import { ROLE_LABELS } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import type { Invitation } from '@/lib/types';
import { CopyField } from '@/components/report/CopyField';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, Select, useToast, type Tone } from '@/components/ui';

function invitationState(i: Invitation): { label: string; tone: Tone } {
  if (i.acceptedAt) return { label: 'Accepted', tone: 'success' };
  if (i.revokedAt) return { label: 'Revoked', tone: 'neutral' };
  if (new Date(i.expiresAt).getTime() < Date.now()) return { label: 'Expired', tone: 'warning' };
  return { label: 'Pending', tone: 'info' };
}

export function AdminInvitationsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const invitations = useQuery({ queryKey: ['admin-invitations'], queryFn: api.adminInvitations });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'PROJECT_ADMIN' | 'ADMIN'>('PROJECT_ADMIN');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => api.adminInvite(email.trim(), role),
    onSuccess: (res) => {
      setInviteUrl(res.inviteUrl);
      toast.success(`Invitation created for ${res.invitation.email}`);
      setEmail('');
      qc.invalidateQueries({ queryKey: ['admin-invitations'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.adminRevokeInvitation(id),
    onSuccess: () => {
      toast.success('Invitation revoked');
      qc.invalidateQueries({ queryKey: ['admin-invitations'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) invite.mutate();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administration"
        title="Invitations"
        description="Invite Project Admins. They can create organizations and invite their own teams."
      />

      <Card>
        <CardHeader title="Invite a Project Admin" />
        <CardBody>
          <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
            <Field label="Email" className="min-w-64 flex-1">
              {(id) => <Input id={id} type="email" placeholder="lead@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />}
            </Field>
            <Field label="Role" className="w-48">
              {(id) => (
                <Select
                  id={id}
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'PROJECT_ADMIN' | 'ADMIN')}
                  options={[
                    { value: 'PROJECT_ADMIN', label: ROLE_LABELS.PROJECT_ADMIN },
                    { value: 'ADMIN', label: ROLE_LABELS.ADMIN },
                  ]}
                />
              )}
            </Field>
            <Button type="submit" loading={invite.isPending} disabled={!email.trim()} icon={<MailPlus className="size-4" />}>
              Create invitation
            </Button>
          </form>
          {inviteUrl && (
            <div className="mt-5 animate-fade-in rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200 dark:bg-emerald-950/30 dark:ring-emerald-900">
              <p className="mb-2 text-sm font-medium text-emerald-800 dark:text-emerald-200">Send this link to the invitee (valid for 7 days):</p>
              <CopyField value={inviteUrl} label="invitation link" />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="All invitations" />
        {invitations.isPending ? (
          <PageLoader />
        ) : invitations.isError ? (
          <div className="p-5">
            <ErrorNotice error={invitations.error} onRetry={() => invitations.refetch()} />
          </div>
        ) : invitations.data.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={<Mail className="size-6" />} title="No invitations yet" />
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {invitations.data.map((i) => {
              const st = invitationState(i);
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5 text-sm">
                  <Mail className="size-4 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{i.email}</div>
                    <div className="text-xs text-slate-500" title={formatDateTime(i.createdAt)}>
                      Sent {timeAgo(i.createdAt)} · expires {formatDateTime(i.expiresAt)}
                    </div>
                  </div>
                  {i.platformRole && <Badge tone="brand">{ROLE_LABELS[i.platformRole]}</Badge>}
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {st.label === 'Pending' && (
                    <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)} icon={<XCircle className="size-4 text-rose-500" />}>
                      Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
